import { Box, Typography, styled, useTheme } from '@mui/material'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import BasicDatePicker from 'src/components/DateTimePicker'
import { GridActionButton } from 'src/components/common/GridActionButton'
import { MUIThemeColors } from 'src/mui-theme'
import { Flex } from 'src/styles/flexComponent'
import { getThemeColor } from 'src/utils'
import {
  DATE_RANGE_PRESETS,
  DateRange,
  DateRangePresetKey,
  getDateRangeForPreset,
} from './unifiedReportGenerator.action'

interface DateRangeSelectorProps {
  range: DateRange
  onChange: (range: DateRange) => void
  disabled?: boolean
  today: Date
}

export const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({ range, onChange, disabled = false, today }) => {
  const { t } = useTranslation('reportGenerator')
  const theme = useTheme()
  const [activePreset, setActivePreset] = useState<DateRangePresetKey | null>(null)

  const applyPreset = (preset: DateRangePresetKey) => {
    setActivePreset(preset)
    onChange(getDateRangeForPreset(preset, today))
  }

  const reset = () => {
    setActivePreset(null)
    onChange({ from: null, to: null })
  }

  const setFrom = (from: Date | null) => {
    setActivePreset(null)
    onChange({ ...range, from })
  }

  const setTo = (to: Date | null) => {
    setActivePreset(null)
    onChange({ ...range, to })
  }

  if (disabled) {
    return (
      <DisabledNote>
        <Typography variant="body2" color={getThemeColor(theme, MUIThemeColors.secondaryDark)}>
          {t('dateNotApplicable')}
        </Typography>
      </DisabledNote>
    )
  }

  return (
    <Flex.Column gap={'12px'}>
      <PresetGrid>
        {DATE_RANGE_PRESETS.map(preset => (
          <GridActionButton
            key={preset}
            buttonText={t(preset)}
            onClick={() => applyPreset(preset)}
            variant="outlined"
            shadowWeight={0}
            width="100%"
            height="32px"
            bgColor={activePreset === preset ? getThemeColor(theme, MUIThemeColors.paleBlue) : undefined}
          />
        ))}
        <GridActionButton
          buttonText={t('reset')}
          onClick={reset}
          variant="outlined"
          shadowWeight={0}
          width="100%"
          height="32px"
        />
      </PresetGrid>

      <Flex.Row gap={'12px'} alignItems={'flex-start'}>
        <FieldBlock>
          <FieldLabel>{t('from')}</FieldLabel>
          <BasicDatePicker isWithoutAppForm name="reportFromDate" value={range.from} onChange={setFrom} />
        </FieldBlock>
        <FieldBlock>
          <FieldLabel>{t('to')}</FieldLabel>
          <BasicDatePicker
            isWithoutAppForm
            name="reportToDate"
            value={range.to}
            onChange={setTo}
            minDate={range.from ?? undefined}
          />
        </FieldBlock>
      </Flex.Row>
    </Flex.Column>
  )
}

const PresetGrid = styled(Box)`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
`

const FieldBlock = styled(Box)`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const FieldLabel = styled(Typography)`
  font-size: 12px;
  font-weight: 600;
`

const DisabledNote = styled(Flex.Row)`
  align-items: center;
  min-height: 80px;
`
